import { getForm, listForms } from '@portal/forms';
import { useMemo, useRef, useState } from 'react';
import { ApiError, submitForm } from '../api/client';
import { FormRenderer } from '../components/FormRenderer';
import { SubmissionReceipt } from '../components/SubmissionReceipt';
import { FormPicker } from './FormPicker';

// The outcome of the last submit attempt is one of: { kind: 'idle' },
// { kind: 'submitted', result }, { kind: 'stale', currentVersion } or
// { kind: 'error', message }.

/** A form with a list wants the wide card: a table of six columns does not fit 50em. */
function cardClass(form) {
  return form?.fields.some((f) => f.widget === 'list') ? 'card card--wide' : 'card';
}

export function SubmitPage({ me }) {
  // The server decides what this user may use; the picker just reflects it. Filtering
  // here is presentation only — POST /api/submissions is what actually enforces it.
  const allowed = useMemo(() => new Set(me.formIds ?? []), [me.formIds]);
  const forms = useMemo(() => listForms().filter((f) => allowed.has(f.id)), [allowed]);
  // No form chosen = the landing page, which lists what this user can open.
  const [formId, setFormId] = useState(undefined);
  const form = formId ? getForm(formId) : undefined;

  const [outcome, setOutcome] = useState({ kind: 'idle' });
  const [fieldErrors, setFieldErrors] = useState({});
  // One key per submission attempt-set: generated when the user first presses
  // Submit, reused if they retry after an error, discarded on success. The gateway
  // uses it to make "retry" mean "the same submission", never a duplicate.
  const idempotencyKey = useRef(undefined);

  const canSubmit = me.roles.includes('Submitter');

  /** Open a form, or pass nothing to go back to the list. Clears any prior attempt. */
  function openForm(id) {
    idempotencyKey.current = undefined;
    setFieldErrors({});
    setOutcome({ kind: 'idle' });
    setFormId(id);
  }

  async function handleSubmit(payload) {
    if (!form) return;
    idempotencyKey.current ??= crypto.randomUUID();
    setFieldErrors({});
    try {
      const result = await submitForm({
        formId: form.id,
        formVersion: form.version,
        payload,
        idempotencyKey: idempotencyKey.current,
      });
      idempotencyKey.current = undefined;
      // Keep the answers: they are the submitter's only copy of what they sent, and
      // nothing on either side of this app stores them.
      setOutcome({ kind: 'submitted', result, payload, form });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'SCHEMA_STALE') {
          const { currentVersion } = error.body;
          setOutcome({ kind: 'stale', currentVersion });
          return;
        }
        if (error.code === 'validation_failed') {
          setFieldErrors(error.fieldErrors());
          setOutcome({ kind: 'error', message: 'Please correct the highlighted fields.' });
          return;
        }
        if (error.code === 'submission_too_long') {
          // The whole submission travels in one capped field at the gateway. The server
          // says by how much it overshoots, so the user has a number to aim for.
          const { length, limit } = error.body;
          const over = Number.isFinite(length - limit) ? length - limit : undefined;
          setOutcome({
            kind: 'error',
            message: over
              ? `Your request is ${over.toLocaleString()} characters over the limit. Shorten the longer free-text answers and try again.`
              : 'Your request is too long. Shorten the longer free-text answers and try again.',
          });
          return;
        }
        if (error.code === 'tenant_not_configured') {
          setOutcome({
            kind: 'error',
            message:
              "Your organisation's account is not fully set up for requests yet. Nothing was sent — please contact NICS and quote this message.",
          });
          return;
        }
        if (error.code === 'destination_not_configured') {
          // The form routes somewhere the server has not been told about. An operator's
          // omission, like the one above: nothing to retry, someone to tell.
          setOutcome({
            kind: 'error',
            message:
              'This form is not fully set up on the server yet. Nothing was sent — please contact NICS and quote this message.',
          });
          return;
        }
        if (error.status === 401) {
          window.location.assign('/?auth_error=state');
          return;
        }
        if (error.status === 403) {
          setOutcome({ kind: 'error', message: 'Your account is not allowed to submit requests.' });
          return;
        }
        if (error.status === 502) {
          setOutcome({
            kind: 'error',
            message:
              'The request could not be delivered right now. Nothing was lost — please try again in a moment.',
          });
          return;
        }
      }
      setOutcome({ kind: 'error', message: 'Something went wrong. Please try again.' });
    }
  }

  if (outcome.kind === 'submitted') {
    // Name and email when we have both, otherwise whichever we have. `/api/me` returns
    // either as null, so a plain template would put "null" on someone's saved PDF.
    const submittedBy =
      me.name && me.email ? `${me.name} (${me.email})` : (me.name ?? me.email ?? undefined);

    return (
      <section className={cardClass(outcome.form)}>
        {outcome.result.dryRun ? (
          // The server is in dry-run mode: the gateway checked everything and filed
          // nothing. Said loudly, so nobody leaves believing a request went in.
          <p className="submitted-note alert alert--warn" role="status">
            Dry run: nothing was filed. This is exactly what would have been sent, under{' '}
            <strong>{outcome.result.accountName}</strong>.
          </p>
        ) : (
          <p className="submitted-note">
            Request submitted — a technician will review it. Save this form now if you would like to
            keep a copy for your records.
          </p>
        )}

        <SubmissionReceipt
          form={outcome.form}
          payload={outcome.payload}
          result={outcome.result}
          submittedBy={submittedBy}
        />

        <div className="actions">
          <button type="button" className="button" onClick={() => window.print()}>
            Save as PDF
          </button>
          <button type="button" className="link" onClick={() => openForm(undefined)}>
            Submit another request
          </button>
          <p className="help print-hint">
            In the dialog, choose the destination <strong>Save as PDF</strong> — or{' '}
            <strong>Save to PDF</strong> in Firefox. It names the file for you.
          </p>
        </div>
      </section>
    );
  }

  if (outcome.kind === 'stale') {
    return (
      <section className="card">
        <h1>This form has been updated</h1>
        <p className="muted">
          The portal was updated while you had this page open (form version {outcome.currentVersion}{' '}
          is now current). Reload to get the latest version, then re-enter your request.
        </p>
        <button type="button" className="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </section>
    );
  }

  if (!form) {
    return <FormPicker forms={forms} canSubmit={canSubmit} onChoose={openForm} />;
  }

  return (
    <section className={cardClass(form)}>
      <p className="crumb">
        <button type="button" className="link" onClick={() => openForm(undefined)}>
          ← All forms
        </button>
      </p>

      <h1>{form.title}</h1>

      {!canSubmit && (
        <p className="alert alert--warn" role="status">
          You can view this form but your account does not have the Submitter role, so you cannot
          submit requests.
        </p>
      )}

      {outcome.kind === 'error' && (
        <p className="alert alert--error" role="alert">
          {outcome.message}
        </p>
      )}

      <FormRenderer
        key={form.id}
        form={form}
        onSubmit={handleSubmit}
        disabled={!canSubmit}
        serverErrors={fieldErrors}
        directorySearch={Boolean(me.features?.directorySearch)}
      />
    </section>
  );
}
